import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  TemplateRef,
  booleanAttribute,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NgControl } from '@angular/forms';
import { SelectModule } from 'primeng/select';

type ComboOptionRecord = Record<string, unknown>;
export type CxSelectValue<T extends ComboOptionRecord> = T | T[keyof T] | null;

let nextComboId = 0;

@Component({
  selector: 'cx-select',
  standalone: true,
  imports: [CommonModule, FormsModule, SelectModule],
  templateUrl: './cx-select.component.html',
  styleUrls: ['./cx-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CxSelectComponent<
  T extends ComboOptionRecord = ComboOptionRecord,
> implements ControlValueAccessor {
  @Input() options: ReadonlyArray<T> = [];
  @Input() optionLabel = 'label';
  @Input() optionValue?: string;
  @Input() optionDisabled?: string;
  @Input() placeholder?: string;
  @Input() appendTo: unknown = 'body';
  @Input({ transform: booleanAttribute }) loading = false;
  @Input({ transform: booleanAttribute }) filter = false;
  @Input({ transform: booleanAttribute }) showClear = false;
  @Input() label?: string;
  @Input() hint?: string;
  @Input() error?: string;
  @Input() styleClass?: string;
  @Input() pt?: Record<string, unknown>;
  @Input() itemTemplate?: TemplateRef<unknown>;
  @Input() selectedItemTemplate?: TemplateRef<unknown>;

  @Input({ transform: booleanAttribute })
  set disabled(value: boolean) {
    this.inputDisabled = value;
    this.cdr.markForCheck();
  }
  get disabled(): boolean {
    return this.inputDisabled;
  }

  @Output() readonly valueChange = new EventEmitter<CxSelectValue<T>>();

  readonly inputId = `cx-select-${nextComboId++}`;
  readonly ngControl = inject(NgControl, { optional: true, self: true });
  private readonly cdr = inject(ChangeDetectorRef);

  private inputDisabled = false;
  private controlDisabled = false;
  private value: CxSelectValue<T> = null;
  private onChange: (value: CxSelectValue<T>) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  get isDisabled(): boolean {
    return this.inputDisabled || this.controlDisabled;
  }

  get isInvalid(): boolean {
    const control = this.ngControl?.control;
    return !!control?.invalid && !!(control.touched || control.dirty);
  }

  get singleValue(): CxSelectValue<T> {
    return this.normalizeSingleValue(this.value);
  }

  get selectOptions(): T[] {
    return this.options as T[];
  }

  onSelectionChange(value: unknown): void {
    if (this.isDisabled) {
      return;
    }

    const normalized = this.normalizeSingleValue(value);
    this.updateValue(normalized);
  }

  onBlur(): void {
    this.onTouched();
  }

  writeValue(value: CxSelectValue<T>): void {
    this.value = this.normalizeSingleValue(value);
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: CxSelectValue<T>) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.controlDisabled = isDisabled;
    this.cdr.markForCheck();
  }

  private updateValue(value: CxSelectValue<T>): void {
    this.value = value;
    this.onChange(value);
    this.valueChange.emit(value);
    this.cdr.markForCheck();
  }

  private normalizeSingleValue(value: unknown): CxSelectValue<T> {
    if (value === null || value === undefined || Array.isArray(value)) {
      return null;
    }

    return value as CxSelectValue<T>;
  }
}
